// Per-tab role context. Each tab holds its own context token in sessionStorage, so a switch in one
// tab can never redirect another tab's writes. The server re-validates the assignment on every request.
const API = process.env.REACT_APP_BACKEND_URL;
const KEYS = ["ctx_token", "ctx_id", "ctx_info", "ctx_diag_session"];

export const readCtx = () => {
  try { return JSON.parse(sessionStorage.getItem("ctx_info") || "null"); } catch { return null; }
};
export const clearCtx = () => KEYS.forEach((k) => sessionStorage.removeItem(k));

export const pendingWrites = { n: 0 };
export const unsavedGuards = new Set();

// Wait for in-flight saves; returns true only if everything saved and no form reports unsaved edits.
export const waitForSaves = async (ms = 6000) => {
  const t0 = Date.now();
  while (pendingWrites.n > 0 && Date.now() - t0 < ms) await new Promise((r) => setTimeout(r, 150));
  const dirty = [...unsavedGuards].some((g) => { try { return g(); } catch { return false; } });
  return pendingWrites.n === 0 && !dirty;
};

export const fetchContext = async () => {
  const r = await fetch(`${API}/api/context/me`, { credentials: "include" });
  return r.ok ? r.json() : null;
};

export const switchTo = async (assignmentId) => {
  const r = await fetch(`${API}/api/context/switch`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ assignment_id: assignmentId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Couldn't switch roles");
  clearCtx();
  ["impersonation_token", "impersonation_email", "impersonation_mode", "impersonation_env", "admin_act_as"]
    .forEach((k) => sessionStorage.removeItem(k));
  if (d.mode === "assignment") {
    const a = d.assignment;
    sessionStorage.setItem("ctx_token", d.session_token);
    sessionStorage.setItem("ctx_id", a.id);
    sessionStorage.setItem("ctx_info", JSON.stringify({ id: a.id, role: a.role, role_label: a.role_label, org_name: a.org_name,
      environment: a.environment, workspace_id: a.workspace_id }));
    if (d.diag_session) sessionStorage.setItem("ctx_diag_session", JSON.stringify(d.diag_session));
  }
  // Full navigation: drops context-specific caches and cancels outdated requests/subscriptions.
  window.location.assign(d.redirect || "/");
};

export const resetMyDemo = async () => {
  const r = await fetch(`${API}/api/context/demo/reset`, { method: "POST", credentials: "include" });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Reset failed");
  return d;
};

export const requestSwitch = (assignment, current) =>
  window.dispatchEvent(new CustomEvent("msx-switch-request", { detail: { assignment, current } }));
export const requestReset = () => window.dispatchEvent(new CustomEvent("msx-reset-request"));

// Called once from App.js: per-tab diag_session for school contexts + write tracking + context headers.
export const installContextShims = () => {
  const ls = window.localStorage;
  const get = Storage.prototype.getItem, set = Storage.prototype.setItem, rm = Storage.prototype.removeItem;
  Storage.prototype.getItem = function (k) {
    if (this === ls && k === "diag_session") { const v = get.call(sessionStorage, "ctx_diag_session"); if (v) return v; }
    return get.call(this, k);
  };
  Storage.prototype.setItem = function (k, v) {
    if (this === ls && k === "diag_session" && get.call(sessionStorage, "ctx_token")) return set.call(sessionStorage, "ctx_diag_session", v);
    return set.call(this, k, v);
  };
  Storage.prototype.removeItem = function (k) {
    if (this === ls && k === "diag_session" && get.call(sessionStorage, "ctx_diag_session")) {
      // Leaving a school screen: a School context ends; a Portfolio context only drops its school-scoped session.
      const info = JSON.parse(get.call(sessionStorage, "ctx_info") || "{}");
      if (info.role === "school_admin") KEYS.forEach((x) => rm.call(sessionStorage, x));
      else rm.call(sessionStorage, "ctx_diag_session");
      return undefined;
    }
    return rm.call(this, k);
  };
};

export const ctxToken = () => sessionStorage.getItem("ctx_token");
export const ctxId = () => sessionStorage.getItem("ctx_id");

export const reportContextError = (status, body) => {
  if ([401, 403, 409, 423].includes(status) && body?.code) window.dispatchEvent(new CustomEvent("msx-context-error", { detail: body?.code ? body : { code: "assignment_revoked", detail: "Your role session ended. Returning you to your account." } }));
};

// Authenticated file download (keeps the tab's active role; plain links would fall back to the home sign-in).
export const downloadFile = async (url, fallbackName = "download.pdf") => {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(typeof d.detail === "string" ? d.detail : "Download failed");
  }
  const cd = r.headers.get("Content-Disposition") || "";
  const name = (cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i) || [])[1] || fallbackName;
  const href = URL.createObjectURL(await r.blob());
  const a = Object.assign(document.createElement("a"), { href, download: decodeURIComponent(name) });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
};
